import { BaseCustomElement } from "brut-js"
import { throttle } from "./utils.js"

class MealSearch extends BaseCustomElement {
  static tagName = "cc-meal-search"

  static observedAttributes = [
    "show-warnings",
    "url",
  ]

  #searcher = null
  #errorMessage = null
  #addNewLink = null

  urlChangedCallback({newValue}) {
    if (this.#searcher) {
      this.#searcher.abort()
    }
    this.#searcher = new Searcher(newValue)
  }

  update() { 
    const searchField = this.querySelector("input[type=search]")
    this.#addNewLink  = this.#requireSelector("a") // assume first is what we want
    this.#errorMessage = new ErrorMessage(this)

    if (!searchField) {
      this.logger.info("No search field element - nothing is going to happen")
      return
    }
    if (!this.#addNewLink) {
      this.logger.info("No 'Add New' link - nothing is going to happen")
      return
    }

    searchField.addEventListener("input",this.#searchInputListener)
    searchField.addEventListener("keyup", this.#handleKeyEvent)
    this.#addNewLink.addEventListener("click", this.#addNew)
    this.addEventListener("keyup",this.#navigateResults)
  }

  close() {
    const results = this.#requireSelector("cc-meal-search-results")
    if (results) {
      results.style.display = "none"
    }
    const searchField = this.querySelector("input[type=search]")
    if (searchField) {
      searchField.value = ""
    }
    this.#errorMessage.hide()
  }

  #searchInputListener = (element) => {
    const searchTerm  = element.target.value
    const results     = this.#requireSelector("cc-meal-search-results")
    const resultsList = this.#requireSelector("cc-meal-search-results ol")
    const addNewSlot  = this.#requireSelector("a slot[name=name]")

    this.#errorMessage.hide()

    if (resultsList) { resultsList.innerHTML = "" }
    if (addNewSlot)  { addNewSlot.innerHTML = "" }

    if (!searchTerm || searchTerm.length < 3) {
      results.display = "none"
      return
    }

    const progress        = this.#requireSelector("cc-meal-search-results progress")
    const noMatches       = this.#requireSelector("cc-meal-search-results [role=status]")

    if (!results || !resultsList || !addNewSlot || !progress || !noMatches) {
      return
    }
    progress.style.visibility = "visible"
    noMatches.style.display   = "none"

    results.style.display = "block"
    addNewSlot.textContent = searchTerm

    if (!this.#searcher) {
      this.logger.info("No searcher defined, likely due to no url set on the element")
      return
    }

    this.#searcher.search(searchTerm).then( (html) => {
      if (html !== null) {
        resultsList.innerHTML = html
        progress.style.visibility = "hidden"
        if (html == "") {
          noMatches.style.display = "block"
        }
        resultsList.querySelectorAll("a").forEach( (a) => {
          a.addEventListener("click",this.#selectMeal)
        })
      }
    }).catch( (e) => {
      this.#errorMessage.show()
      progress.style.visibility = "hidden"
      console.warn(e)
    })
  }

  #navigateResults = (event) => {
    let movement
    if (event.key == "ArrowDown" || event.key == "Down") {
      movement = "next"
    }
    else if (event.key == "ArrowUp" || event.key == "Up") {
      movement = "previous"
    }

    if (movement) {
      let elementToFocus
      this.querySelectorAll("a").forEach( (a,index,list) => {
        if (document.activeElement == a) {
          if (movement == "next") {
            elementToFocus = list[index+1]
          }
          else {
            elementToFocus = list[index-1]
          }
        }
      })
      if (elementToFocus) {
        elementToFocus.focus()
      }
      else {
        this.#addNewLink.focus()
      }
    }
  }

  #handleKeyEvent = (event) => {
    if (event.key == "Enter") {
      this.#addNew(event,this.#addNewLink)
    }
  }

  #addNew = (event,currentTarget) => {
    event.preventDefault()
    currentTarget = currentTarget || event.currentTarget
    const nameSlot = currentTarget.querySelector("slot[name=name]")
    if (!nameSlot) {
      this.logger.info("event target had no slot[name=name] %o",currentTarget)
      return
    }
    this.dispatchEvent(
      new CustomEvent(
        "addnew",
        {
          cancelable: false,
          bubbles: true,
          detail: { name: nameSlot.textContent }
        })
    )
  }

  #selectMeal = (event) => {
    event.preventDefault()
    const li = event.currentTarget.parentElement
    if (!li) {
      this.logger.info("Something is wrong, no parent?!?!")
      return
    }
    const ccMeal = li.querySelector("cc-meal")
    if (!ccMeal) {
      this.logger.info("Expected a <cc-meal>")
      return
    }
    const mealMetadata = {
      meal_id: ccMeal.getAttribute("meal-id")
    }
    ccMeal.querySelectorAll("dl dt").forEach( (dt) => {
      const key = (dt.textContent || "").trim()
      const dd = dt.nextElementSibling
      if (dd && dd.tagName == "DD") {
        mealMetadata[key] = dd.textContent
      }
      else {
        this.logger.info("For key %s, no <dd> was following it",key)
      }
    })
    this.dispatchEvent(
      new CustomEvent(
        "mealselected",
        {
          cancelable: false,
          bubbles: true,
          detail: mealMetadata,
        }
      )
    )
  }

  #requireSelector(selector) {
    const element = this.querySelector(selector)
    if (!element) {
      this.logger.info("Could not find element with selector '%s' - this element won't work",selector)
    }
    return element
  }
}

class ErrorMessage {
  constructor(element) {
    this.element = element.querySelector("[role='alert']")
  }

  show() {
    if (this.element) {
      this.element.style.display = "block"
    }
  }
  hide() {
    if (this.element) {
      this.element.style.display = "none"
    }
  }
}

class Searcher {

  constructor(url) {
    this.headers = new Headers()
    this.headers.append("X-Requested-With","XMLHttpRequest")
    this.url = this.#ensureNoQuestionMark(url)
  }

  abort() {
    if (this.abortController) {
      this.abortController.abort("new-search")
      this.abortController = null
    }
  }

  #abortedDueToNewSearch(signal) {
    return signal.aborted && signal.reason == "new-search"
  }

  search(searchTerm) {
    const searchParams = new URLSearchParams()
    searchParams.set("search",searchTerm)
    searchParams.set("show_want_to_repeat","true")
    searchParams.set("show_would_repeat","true")
    const url = this.url + "?" + searchParams.toString()

    this.abort()

    this.abortController = new AbortController()
    const signal = this.abortController.signal

    const request = new Request(
      url,
      {
        headers: this.headers,
        method: "get",
        signal: AbortSignal.any([
          AbortSignal.timeout(5000),
          signal,
        ])
      }
    )

    return fetch(request).then( (response) => {
      if (response.ok) {
        return response.text().then( (html) => {
          return Promise.resolve((html || "").trim())
        })
      }
      else {
        return Promise.reject({ response: response })
      }
    }).catch( (e) => {
      if (this.#abortedDueToNewSearch(signal)) {
        console.log("New search replaced this one, ignoring: %o",e)
        return Promise.resolve(null)
      }
      else {
        return Promise.reject({ error: e })
      }
    })
  }

  #ensureNoQuestionMark(url) {
    if (url.endsWith("?")) {
      return url.slice(0,-1)
    }
    return url
  }

}

export default MealSearch
